import React, { useEffect, useState } from "react";
import { Col, Row, Spinner } from "reactstrap";
import { getAiVisibilityHighlights } from "../../services/aiVisibilityApi";

const StatTile = ({ value, label, variant }) => (
	<div className={`crawl-stat-tile crawl-stat-tile--${variant} text-center p-3 rounded`}>
		<div className="crawl-stat-tile__value fw-bold">{value ?? 0}</div>
		<div className="crawl-stat-tile__label small text-muted mt-1">{label}</div>
	</div>
);

const ExternalUrlLink = ({ href, label = "View" }) => (
	<a
		href={href}
		target="_blank"
		rel="noopener noreferrer"
		className="small text-primary text-nowrap"
	>
		{label} <i className="fas fa-external-link-alt ms-1" />
	</a>
);

const CrawlUrlLink = ({ url, className = "text-primary crawl-url-link" }) => (
	<a
		href={url}
		target="_blank"
		rel="noopener noreferrer"
		className={className}
		title={url}
	>
		<i className="fas fa-external-link-alt me-1 flex-shrink-0" />
		<span className="crawl-url-text">{url}</span>
	</a>
);

const HighlightSection = ({
	title,
	icon,
	iconClassName = "text-primary",
	description,
	children,
}) => (
	<section className="crawl-highlight-section">
		<div className="crawl-highlight-section__header">
			<h6 className="crawl-highlight-section__title">
				<i className={`fas ${icon} ${iconClassName}`} />
				{title}
			</h6>
			{description && <p className="crawl-highlight-section__desc">{description}</p>}
		</div>
		<div className="crawl-highlight-section__body">{children}</div>
	</section>
);

const AuditHighlightsPanel = ({ websiteId, auditId }) => {
	const [highlights, setHighlights] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	useEffect(() => {
		if (!websiteId || !auditId) return undefined;
		let cancelled = false;
		setLoading(true);
		setError(null);
		setHighlights(null);
		getAiVisibilityHighlights(websiteId, auditId).then((result) => {
			if (cancelled) return;
			setLoading(false);
			if (!result.ok) {
				setError(result.message);
				return;
			}
			setHighlights(result.highlights);
		});
		return () => {
			cancelled = true;
		};
	}, [websiteId, auditId]);

	if (loading) {
		return (
			<div className="crawl-highlights-state text-center py-4">
				<Spinner />
			</div>
		);
	}

	if (error || !highlights) {
		return (
			<div className="crawl-highlights-state text-center py-4 text-muted">
				<i className="fas fa-exclamation-circle fa-2x mb-2 d-block" />
				<p className="mb-0">{error || "No crawl data available yet"}</p>
			</div>
		);
	}

	const meta = highlights.crawl_metadata || {};
	const pagesByType = highlights.pages_by_type || [];
	const keyPages = highlights.key_pages_found || [];
	const errors = highlights.errors || [];

	return (
		<div className="ai-visibility-crawl-highlights">
			<HighlightSection
				title="Crawl Overview"
				icon="fa-globe"
				description="Summary of website crawl and discovery"
			>
				<Row className="g-3">
					<Col xs={6} md={3}>
						<StatTile
							value={meta.total_pages_crawled}
							label="Pages Crawled"
							variant="blue"
						/>
					</Col>
					<Col xs={6} md={3}>
						<StatTile value={keyPages.length} label="Key Pages" variant="green" />
					</Col>
					<Col xs={6} md={3}>
						<StatTile value={meta.crawl_depth_used} label="Max Depth" variant="purple" />
					</Col>
					<Col xs={6} md={3}>
						<StatTile value={meta.pages_with_errors} label="Errors" variant="orange" />
					</Col>
				</Row>
				{meta.average_word_count != null && (
					<div className="crawl-word-count-stat mt-3 p-3 rounded">
						<div className="small text-muted">Average Word Count</div>
						<div className="crawl-word-count-stat__value fw-semibold mb-0">
							{Number(meta.average_word_count).toLocaleString()}
						</div>
					</div>
				)}
			</HighlightSection>

			<HighlightSection title="Robots.txt & Sitemap Status" icon="fa-file-alt">
				<div
					className={`crawl-highlight-row crawl-highlight-row--${
						meta.robots_txt_found ? "found" : "missing"
					} d-flex align-items-center justify-content-between gap-2 p-3 rounded`}
				>
					<div className="d-flex align-items-center gap-3 min-w-0">
						<i
							className={`fas ${
								meta.robots_txt_found ? "fa-check-circle text-success" : "fa-times-circle text-muted"
							}`}
						/>
						<div>
							<div className="fw-medium">robots.txt</div>
							<div className="small text-muted">
								{meta.robots_txt_found ? "Found" : "Not found"}
							</div>
						</div>
					</div>
					{meta.robots_txt_url && <ExternalUrlLink href={meta.robots_txt_url} />}
				</div>
				<div
					className={`crawl-highlight-row crawl-highlight-row--${
						meta.sitemap_found ? "found" : "missing"
					} d-flex align-items-center justify-content-between gap-2 p-3 rounded`}
				>
					<div className="d-flex align-items-center gap-3 min-w-0">
						<i
							className={`fas ${
								meta.sitemap_found ? "fa-check-circle text-success" : "fa-times-circle text-muted"
							}`}
						/>
						<div>
							<div className="fw-medium">sitemap.xml</div>
							<div className="small text-muted">
								{meta.sitemap_found
									? `Found (${meta.sitemap_urls_count ?? 0} URLs)`
									: "Not found"}
							</div>
						</div>
					</div>
					{meta.sitemap_url && <ExternalUrlLink href={meta.sitemap_url} />}
				</div>
			</HighlightSection>

			{pagesByType.length > 0 && (
				<HighlightSection
					title="Pages by Type"
					icon="fa-link"
					description="Distribution of discovered pages"
				>
					{pagesByType.map((typeGroup) => (
						<div key={typeGroup.page_type} className="crawl-page-type-group">
							<div className="crawl-page-type-group__header">
								<span className="fw-medium text-capitalize">{typeGroup.page_type}</span>
								<span className="crawl-page-type-group__count">
									{typeGroup.count} pages
								</span>
							</div>
							{(typeGroup.sample_urls || []).length > 0 && (
								<div className="small">
									{typeGroup.sample_urls.map((url, idx) => (
										<div key={idx} className="crawl-url-row">
											<CrawlUrlLink url={url} />
										</div>
									))}
								</div>
							)}
						</div>
					))}
				</HighlightSection>
			)}

			{keyPages.length > 0 && (
				<HighlightSection
					title="Key Pages Found"
					icon="fa-check-circle"
					iconClassName="text-success"
					description="Important pages for AEO analysis"
				>
					{keyPages.map((page, idx) => (
						<div key={idx} className="crawl-key-page-row p-3 rounded">
							<div className="fw-medium">{page.title}</div>
							<span className="badge bg-success text-capitalize mt-1">
								{page.page_type}
							</span>
							<div className="mt-1 crawl-url-row">
								<CrawlUrlLink url={page.url} className="small text-primary crawl-url-link" />
							</div>
						</div>
					))}
				</HighlightSection>
			)}

			{errors.length > 0 && (
				<HighlightSection
					title="Crawl Errors"
					icon="fa-exclamation-circle"
					description={`${errors.length} pages could not be crawled`}
				>
					{errors.map((err, idx) => (
						<div key={idx} className="crawl-error-row p-3 rounded">
							<div className="small fw-medium text-danger crawl-url-text" title={err.url}>
								{err.url}
							</div>
							<div className="small text-danger mt-1">{err.error_message}</div>
						</div>
					))}
				</HighlightSection>
			)}

			<HighlightSection title="Link Statistics" icon="fa-link">
				<Row className="g-3">
					<Col xs={6}>
						<StatTile
							value={meta.total_internal_links}
							label="Internal Links"
							variant="blue"
						/>
					</Col>
					<Col xs={6}>
						<StatTile
							value={meta.total_external_links}
							label="External Links"
							variant="purple"
						/>
					</Col>
				</Row>
			</HighlightSection>
		</div>
	);
};

export default AuditHighlightsPanel;
